//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import InputError from '@/components/UI/Form/Error';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

import ReactSelect from '@/components/UI/Form/ReactSelect';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { PercentFormatDisplay } from '@/components/UI/NumberFormatDisplay/PercentFormatDisplay';
import QuestionMarkTooltip from '@/components/UI/QuestionMarkTooltip';
import type { ReactSelectOption } from '@/types/Constants';
import { Sources } from '../constants';
//Assets and Miscellaneous
import { initialValues, sourcesWithOnlyAmountField } from './constants';
import type { IProposedFinance } from './types';

//Component Begin

const ProposedFinance = ({ formValues }: { formValues: any }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editFinanceId, setEditFinanceId] = useState<string>('');
  const [selectedFinance, setSelectedFinance] = useState<IProposedFinance>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [finance, setFinance] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setFinance(initialValues);
  };

  const editFinanceHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(finance).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...finance[next],
          value: f[next],
          isTouched: !!f[next],
        },
      };
    }, {});
    setFinance(formData);
    setEditFinanceId(f.id);
  };

  const updateFinanceHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(finance).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: finance[next].value,
            id: editFinanceId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step3: {
          ...formValues,
          section2: data.step3.section2.map((p: any) => {
            if (p.id === editFinanceId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeFinanceHandler = (finance: IProposedFinance) => {
    setShowRemoveConfirm(true);
    setSelectedFinance(finance);
  };

  const confirmRemoveFinance = async () => {
    setShowRemoveConfirm(false);
    const filteredTenant = data.step3.section2.filter((finance: any) => finance.id !== selectedFinance?.id);
    updateData({
      ...data,
      step3: {
        ...formValues,
        section2: filteredTenant,
      },
    });
    setFinance((prevState) => ({
      ...prevState,
      source: initialValues.source,
    }));

    if (selectedFinance?.id === editFinanceId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveFinance = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...finance };
    let formDataKeys = Object.keys(formData);

    if (sourcesWithOnlyAmountField.includes(formData.source.value)) {
      formDataKeys = formDataKeys.filter((key) => key === 'amount');
    } else {
      formDataKeys = formDataKeys.filter((key) => key !== 'amount');
    }

    const formDataErrors = formDataKeys.reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setFinance((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddFinanceHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(finance).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: finance[next].value };
        },
        {}
      );

      const section2 = [...data.step3.section2];
      const { amount, source, ...otherFields } = formattedData;
      const newRecord = sourcesWithOnlyAmountField.includes(formattedData.source as string)
        ? {
            source,
            amount,
            id: uuidv4(),
          }
        : { ...otherFields, source, id: uuidv4() };

      if (formattedData.source === 'Façade Program Assistance') {
        section2.unshift(newRecord);
      } else {
        section2.push(newRecord);
      }

      updateData({
        ...data,
        step3: {
          ...formValues,
          section2,
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setFinance, finance);

  const calculateTotalCost = (data: any) => {
    let total = 0;
    if (data.eligibleCost) {
      total += data.eligibleCost;
    }

    if (data.ineligibleCost) {
      total += data.ineligibleCost;
    }
    return total;
  };

  useEffect(() => {
    setFinance((prevState) => ({
      ...prevState,
      totalCost: {
        ...prevState.totalCost,
        value: calculateTotalCost({
          eligibleCost: finance?.eligibleCost?.value,
          ineligibleCost: finance?.ineligibleCost?.value,
        }),
        isTouched: true,
      },
    }));
  }, [finance?.eligibleCost?.value, finance?.ineligibleCost?.value, calculateTotalCost]);

  const selectedSources = data.step3.section2.map((item: IProposedFinance) => item.source);

  const isFirstRecordFacadeProgram = data?.step3?.section2[0]?.source === 'Façade Program Assistance';

  const getSourceOptions = () => {
    return !isFirstRecordFacadeProgram
      ? Sources.filter((source) => source === 'Façade Program Assistance').map((source) => ({
          label: source,
          value: source,
        }))
      : Sources.filter((source) => !selectedSources.includes(source)).map((source) => ({
          label: source,
          value: source,
        }));
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove finance'
          message={`You are about to remove ${selectedFinance?.source} finance. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveFinance}
          decline={cancelRemoveFinance}
        />
      )}
      <section className='space-y-4'>
        <div>
          <h3>Proposed Financing</h3>
          <p className='block text-sm text-neutralColor-normal'>
            Other sources of funding must be equal to or greater than 20% of the Façade Program Assistance amount.
          </p>
        </div>
        {data.step3?.section2.length > 0 && (
          <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Source</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Amount</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>% of Project Funding</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Rate</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Term (months)</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Eligible Costs</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Ineligible Costs</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Total Costs</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step3.section2.map((finance: IProposedFinance) => (
                  <tr key={finance.id} className={`${finance.id === editFinanceId ? 'bg-neutralColor-lighter' : ''}`}>
                    <td className='p-4'>{finance.source.label}</td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={finance.amount ?? 0} />
                    </td>
                    <td className='p-4'>
                      <PercentFormatDisplay value={finance.projectFundingPercentage ?? 0} />
                    </td>
                    <td className='p-4'>
                      <PercentFormatDisplay value={finance.rate ?? 0} />
                    </td>
                    <td className='p-4'>{finance.term}</td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={finance.eligibleCost ?? 0} />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={finance.ineligibleCost ?? 0} />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={calculateTotalCost(finance)} />
                    </td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div className='cursor-pointer hover:text-blue-500' onClick={() => editFinanceHandler(finance)}>
                        Edit
                      </div>
                      <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeFinanceHandler(finance)}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <ReactSelect
              label='Source'
              id='source'
              name='source'
              placeholder={isFirstRecordFacadeProgram ? 'Select or type new source...' : 'Select Source'}
              options={getSourceOptions()}
              value={
                finance.source.value
                  ? {
                      label: finance.source.value,
                      value: finance.source.value,
                    }
                  : null
              }
              error={finance?.source?.error}
              onChange={(option) => {
                inputChangedHandler((option as ReactSelectOption | null)?.value, 'source');
              }}
              onBlur={(_e) => {
                inputChangedHandler(finance.source.value, 'source');
              }}
              isCreatable={isFirstRecordFacadeProgram}
              isClearable={isFirstRecordFacadeProgram}
              isValidNewOption={(value) => !!value}
              formatCreateLabel={(input) => `Other "${input}"`}
            />
          </div>
          {sourcesWithOnlyAmountField.includes(finance.source.value) || !isFirstRecordFacadeProgram ? (
            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
              <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='amount'>
                Amount
              </label>
              <NumberFormat
                name='amount'
                id='amount'
                value={finance.amount.value ?? ''}
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                valueIsNumericString
                maxLength={21}
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'amount');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(finance.amount.value, e.target.name);
                }}
              />
              {finance?.amount?.error && <InputError>{finance?.amount?.error}</InputError>}
            </div>
          ) : (
            <>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='term'>
                  Term (months)
                </label>
                <NumberFormat
                  name='term'
                  id='term'
                  value={finance.term.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  valueIsNumericString
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'term');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(finance.term.value, e.target.name);
                  }}
                />
                {finance?.term?.error && <InputError>{finance?.term?.error}</InputError>}
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='projectFundingPercentage'>
                  % of Project Funding
                </label>
                <NumberFormat
                  name='projectFundingPercentage'
                  id='projectFundingPercentage'
                  value={finance.projectFundingPercentage.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  valueIsNumericString
                  maxLength={3}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'projectFundingPercentage');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(finance.projectFundingPercentage.value, e.target.name);
                  }}
                />
                {finance?.projectFundingPercentage?.error && <InputError>{finance?.projectFundingPercentage?.error}</InputError>}
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
                  Rate (%)
                </label>
                <NumberFormat
                  name='rate'
                  id='rate'
                  value={finance.rate.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  valueIsNumericString
                  maxLength={3}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'rate');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(finance.rate.value, e.target.name);
                  }}
                />
                {finance?.rate?.error && <InputError>{finance?.rate?.error}</InputError>}
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <div className='flex items-center w-full gap-1'>
                  <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
                    Eligible Cost ($)
                  </label>
                  <QuestionMarkTooltip
                    toolTip={
                      <ul className='list-disc p-2 ml-2'>
                        <li>Repair/replace/preserve historically significant architectural details</li>
                        <li>Cornice Repair</li>
                        <li>Masonry repair and tuckpointing</li>
                        <li>Storefront (re)construction</li>
                        <li>Window and door repair/replacement</li>
                        <li>Exterior painting and stucco</li>
                        <li>Permanent exterior lighting</li>
                        <li>Permanent exterior signage integrated into the storefront design</li>
                        <li>Murals if they are professionally painted, provide an important aesthetic improvement</li>
                      </ul>
                    }
                  />
                </div>
                <NumberFormat
                  name='eligibleCost'
                  id='eligibleCost'
                  value={finance.eligibleCost.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  valueIsNumericString
                  prefix={'$ '}
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'eligibleCost');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(finance.eligibleCost.value, e.target.name);
                  }}
                />
                {finance?.eligibleCost?.error && <InputError>{finance?.eligibleCost?.error}</InputError>}
              </div>
              <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                <div className='flex items-center w-full gap-1'>
                  <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='originalAmount'>
                    Ineligible Cost ($)
                  </label>
                  <QuestionMarkTooltip
                    toolTip={
                      <ul className='list-disc p-2 ml-2'>
                        <li>Any improvements not visible from the public right-of-way or publicly owned space</li>
                        <li>Landscaping and fencing</li>
                        <li>Non-visible mechanical equipment screening</li>
                        <li>Interior improvements/remodeling</li>
                        <li>Temporary, portable, or non-permanent improvements</li>
                        <li>New construction</li>
                        <li>Business operations-related costs</li>
                        <li>Property acquisition, expansion of building area, or conversion of building use</li>
                        <li>Normal maintenance and repair</li>
                        <li>Roof repair</li>
                      </ul>
                    }
                  />
                </div>
                <NumberFormat
                  name='ineligibleCost'
                  id='ineligibleCost'
                  value={finance.ineligibleCost.value ?? ''}
                  className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                  thousandSeparator={true}
                  decimalScale={2}
                  valueIsNumericString
                  prefix={'$ '}
                  maxLength={21}
                  onValueChange={(target, s) => {
                    if (s.source !== 'prop') {
                      inputChangedHandler(target.floatValue, 'ineligibleCost');
                    }
                  }}
                  onBlur={(e: any) => {
                    inputChangedHandler(finance.ineligibleCost.value, e.target.name);
                  }}
                />
                {finance?.ineligibleCost?.error && <InputError>{finance?.ineligibleCost?.error}</InputError>}
              </div>
            </>
          )}
        </div>
        {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddFinanceHandler} />}
        {isEditing && (
          <div className='flex gap-4'>
            <Button label='Save' type='button' btnStyle='primary' onClick={updateFinanceHandler} />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditFinanceId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default ProposedFinance;
